import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { liveObject } from '../../../../core/i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BoardField, BoardManager, DynamicBoardCard } from '../../board-v3.types';
import { BaseService } from 'src/app/services/base.service';
import { BoardService } from '../../../../services/board.service';
import { TranslateService } from '@ngx-translate/core';
import { BoardV3RealtimeService } from '../../board-v3-realtime.service';

@Component({
  selector: 'app-board-v3-card-view-settings',
  templateUrl: './card-view-settings.component.html',
  styleUrls: ['./card-view-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CardViewSettingsComponent implements OnInit, OnChanges {
  @Input() isSaving: boolean = false;
  @Input() errorMessage: string = '';
  @Input() funnelId: string | null = null;
  @Input() activeKeys: string[] = [];

  @Output() apply = new EventEmitter<string[]>();
  @Output() close = new EventEmitter<{ changed: boolean } | undefined>();

  private readonly baseService = inject(BaseService);
  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly realtimeService = inject(BoardV3RealtimeService);
  enabledFields = new Set<string>();
  previewViewFields: string[] = [];
  fields!: BoardField[];

  ngOnInit(): void {
    this.enabledFields = new Set(this.activeKeys ?? []);
    this.syncPreviewViewFields();
    this.loadFields();
    this.subscribeToRealtime();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeKeys'] && !changes['activeKeys'].firstChange) {
      this.enabledFields = new Set(this.activeKeys ?? []);
      this.syncPreviewViewFields();
    }

    if (changes['funnelId'] && !changes['funnelId'].firstChange) this.loadFields();
  }

  private loadFields(): void {
    if (!this.funnelId) return;

    this.boardService
      .getBoardFields(this.funnelId, 'FUNNEL')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fields) => {
          this.fields = fields;
          this.syncPreviewViewFields();
        },
      });
  }

  private subscribeToRealtime(): void {
    if (!this.funnelId) return;

    this.realtimeService
      .fieldsUpdated$(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.fields = event.fields ?? [];
        if (event.funnel?.view_fields) {
          this.enabledFields = new Set(event.funnel.view_fields);
        }
        this.syncPreviewViewFields();
      });

    this.realtimeService
      .funnelUpdatedById$(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ funnel }) => {
        this.enabledFields = new Set(funnel?.view_fields ?? []);
        this.syncPreviewViewFields();
      });
  }

  previewCard: DynamicBoardCard = liveObject(() => ({
    id: 'preview',
    active: true,
    created_by: null,
    created_at: '2026-05-13T20:49:01.242Z',
    updated_by: null,
    updated_at: new Date().toISOString(),
    deleted_by: null,
    deleted_at: null,
    company_id: '',
    funnel_id: '',
    column_id: '',
    name: this.translateService.instant('board_v3_preview_card_name'),
    description: null,
    color: { text: '#000000', background: '#ffffff' },
    position: 1000,
    custom_fields: {},
    price: 48200,
    currency: 'UAH',
    assigned_id: this.baseService.user.id,
    phones: [
      { id: '1', phone: '+380 67 123 45 67' },
      { id: '2', phone: '+380 50 987 65 43' },
    ],
    emails: [{ id: '1', email: 'demo@example.com' }],
    addresses: [
      {
        id: '1',
        country_code: 'UA',
        city: 'Київ',
        street: 'вулиця Хрещатик',
        building: '22',
        formatted_address: 'вулиця Хрещатик, 22, Київ, Україна, 02000',
      },
    ],
    rn: '1',
    created_by_user: this.baseService.user as any as BoardManager,
    updated_by_user: this.baseService.user as any as BoardManager,
    assigned_id_user: this.baseService.user as any as BoardManager,
    contacts: [
      {
        id: 'preview-contact',
        company_id: '',
        first_name: this.translateService.instant('board_v3_preview_contact_first_name'),
        last_name: this.translateService.instant('board_v3_preview_contact_last_name'),
        middle_name: null,
        phones: [],
        emails: [],
        cards: [],
        active: true,
        created_by: null,
        created_at: '2026-05-13T20:49:01.242Z',
        updated_by: null,
        updated_at: null,
      },
    ],
    company: {
      id: 'preview-company',
      company_id: '',
      name: this.translateService.instant('board_v3_preview_company_name'),
      phones: [],
      emails: [],
      addresses: [],
      cards: [],
      active: true,
      created_by: null,
      created_at: '2026-05-13T20:49:01.242Z',
      updated_by: null,
      updated_at: null,
    },}));

  private readonly virtualFields: BoardField[] = liveObject(() => ([
    {
      id: 'clients',
      identifier: 'clients',
      name: this.translateService.instant('board_v3_quick_create_clients'),
      input_type: 'clients',
      position: 9998,
      icon: 'group',
    },
    {
      id: 'actions',
      identifier: 'actions',
      name: this.translateService.instant('board_v3_quick_actions_field'),
      input_type: 'actions',
      position: 9999,
      icon: 'bolt',
    },
  ]));

  get allFields(): BoardField[] {
    return [...(this.fields || []), ...this.virtualFields];
  }

  get totalCount(): number {
    return this.allFields.length;
  }

  get enabledCount(): number {
    return this.allFields.filter((f) => this.isEnabled(f)).length;
  }

  fieldKey(field: BoardField): string {
    return String(field.identifier ?? field.id ?? field.name ?? '');
  }

  isEnabled(field: BoardField): boolean {
    return this.enabledFields.has(this.fieldKey(field));
  }

  setEnabled(field: BoardField, value: boolean): void {
    const key = this.fieldKey(field);
    if (value) this.enabledFields.add(key);
    else this.enabledFields.delete(key);
    this.syncPreviewViewFields();
  }

  private syncPreviewViewFields(): void {
    this.previewViewFields = this.allFields
      .filter((f) => this.enabledFields.has(this.fieldKey(f)))
      .map((f) => this.fieldKey(f));
  }

  onApplyClick(): void {
    if (this.isSaving) return;
    const orderedKeys = this.allFields.filter((f) => this.isEnabled(f)).map((f) => this.fieldKey(f));
    this.apply.emit(orderedKeys);
  }

  onClose(): void {
    if (!this.isSaving) this.close.emit();
  }
}
